/**
 * Cross-Cloud Tag-Namespace Canonicaliser (Patent Sub-system U)
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Loader2, FlaskConical, Tags } from 'lucide-react';
import DemoDataBanner from '../components/DemoDataBanner';
import BrandTitle from '../components/BrandTitle';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TagCanon = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => { axios.get(`${API_URL}/tag-canon/demo`).then(r => setData(r.data)).finally(() => setLoading(false)); }, []);

  const cloudColor = { aws: 'border-orange-500/40 text-orange-200', gcp: 'border-emerald-500/40 text-blue-200', azure: 'border-emerald-500/40 text-cyan-200', k8s: 'border-indigo-500/40 text-indigo-200' };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100" data-testid="tag-canon-page">
      <Helmet><title>Tag-Namespace Canonicaliser | CARTIEAI</title></Helmet>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        <DemoDataBanner />
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-widest text-teal-300 border border-teal-500/30 rounded-md px-2 py-1 mb-5" data-testid="tc-patent-badge">
            <FlaskConical className="w-3 h-3" /> Proprietary engine
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight leading-[1.05]"><BrandTitle>Tag Canonicaliser</BrandTitle></h1>
          <p className="mt-4 text-base sm:text-lg text-slate-300 leading-relaxed">AWS uses <code className="text-teal-300">tag:cost_center</code>. GCP uses <code className="text-teal-300">labels.cost-center</code>. Azure uses <code className="text-teal-300">tags.CostCenter</code>. K8s uses <code className="text-teal-300">cartie.ai/cost-center</code>.</p>
          <p className="mt-3 text-sm text-slate-400">We canonicalise them all with a learned pattern catalog + LLM-classifier-assisted disambiguation. No more &quot;82% unallocated&quot; because tags don&apos;t match across clouds.</p>
        </div>

        {loading && <Loader2 className="w-8 h-8 animate-spin mx-auto my-12 text-slate-500" />}

        {data && (
          <>
            <div className="mt-10 grid grid-cols-3 gap-3" data-testid="tc-summary">
              <Card className="bg-slate-900/60 border border-emerald-500/30"><CardContent className="p-4"><div className="text-[10px] uppercase tracking-widest text-emerald-300">Canonicalised</div><div className="text-2xl font-bold text-white mt-1">{data.canonicalised} / {data.total}</div></CardContent></Card>
              <Card className="bg-slate-900/60 border border-teal-500/30"><CardContent className="p-4"><div className="text-[10px] uppercase tracking-widest text-teal-300">High confidence</div><div className="text-2xl font-bold text-white mt-1">{data.high_confidence}</div></CardContent></Card>
              <Card className="bg-slate-900/60 border border-amber-500/30"><CardContent className="p-4"><div className="text-[10px] uppercase tracking-widest text-amber-300">Needs LLM review</div><div className="text-2xl font-bold text-white mt-1">{data.needs_llm_review}</div></CardContent></Card>
            </div>

            <Card className="mt-8 bg-slate-900/60 border border-white/10">
              <CardHeader><CardTitle className="text-white text-lg flex items-center gap-2"><Tags className="w-5 h-5 text-teal-300" /> Canonicalisation results</CardTitle><CardDescription className="text-slate-400">Same logical concept, 7 different spellings across clouds → collapsed to one canonical name.</CardDescription></CardHeader>
              <CardContent>
                <table className="w-full text-sm" data-testid="tc-results">
                  <thead className="text-[10px] uppercase tracking-widest text-slate-500">
                    <tr className="border-b border-white/10"><th className="text-left p-2">Cloud</th><th className="text-left p-2">Raw tag</th><th className="text-left p-2">→ Canonical</th><th className="text-right p-2">Conf</th></tr>
                  </thead>
                  <tbody>
                    {data.results.map((r, i) => (
                      <tr key={i} className="border-b border-white/5 hover:bg-slate-950/60">
                        <td className="p-2"><Badge variant="outline" className={`text-[10px] font-mono ${cloudColor[r.cloud] || 'border-white/10 text-slate-400'}`}>{r.cloud}</Badge></td>
                        <td className="p-2 text-xs text-slate-300 font-mono">{r.tag_key}</td>
                        <td className="p-2 text-xs"><span className="text-teal-300 font-mono font-semibold">{r.canonical_name || <span className="text-red-300">unmatched</span>}</span></td>
                        <td className={`p-2 text-xs text-right font-mono ${r.confidence >= 0.85 ? 'text-emerald-300' : r.confidence > 0 ? 'text-amber-300' : 'text-slate-500'}`}>{r.confidence}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </div>
  );
};

export default TagCanon;
